import { computed } from 'vue';

import { useAuthenticationStore } from 'src/stores/authentication';

import {
	InstanceAuthenticationConfiguration,
	normalizeInstanceAuthenticationConfiguration,
	normalizeInstanceAuthenticationConfigurations,
} from 'src/models/authentication';

import {
	ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY,
	INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
} from 'src/services/authentication';

export default function useAuthentication() {
	const initialize = () => {
		const authenticationStore = useAuthenticationStore();
		authenticationStore.setActiveInstanceAuthenticationConfiguration(
			activeInstanceAuthenticationConfiguration.value
		);
	};

	const addInstanceAuthenticationConfigurationAndActivate = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	) => {
		addInstanceAuthenticationConfiguration(instanceAuthenticationConfiguration);
		localStorage.setItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY, instanceAuthenticationConfiguration.name);
	};

	const addInstanceAuthenticationConfiguration = (
		instanceAuthenticationConfiguration: InstanceAuthenticationConfiguration
	) => {
		const normalizedInstanceAuthenticationConfiguration = normalizeInstanceAuthenticationConfiguration(
			instanceAuthenticationConfiguration
		);
		let allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurations.value;

		allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurationsData.filter(
			(instanceAuthenticationConfig) =>
				instanceAuthenticationConfig.name !== normalizedInstanceAuthenticationConfiguration.name
		);
		allInstanceAuthenticationConfigurationsData.push(normalizedInstanceAuthenticationConfiguration);

		localStorage.setItem(
			INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
			JSON.stringify(allInstanceAuthenticationConfigurationsData)
		);
	};

	const removeInstanceAuthenticationConfigurationName = (instanceAuthenticationConfigurationName: string) => {
		let allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurations.value;

		if (allInstanceAuthenticationConfigurationsData !== null) {
			const activeInstanceAuthenticationConfigurationData = activeInstanceAuthenticationConfiguration.value;

			if (activeInstanceAuthenticationConfigurationData !== null) {
				allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurationsData.filter(
					(instanceConfiguration) => instanceConfiguration.name !== instanceAuthenticationConfigurationName
				);

				localStorage.setItem(
					INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
					JSON.stringify(allInstanceAuthenticationConfigurationsData)
				);
				if (
					localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) ===
					instanceAuthenticationConfigurationName
				) {
					localStorage.setItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY, '');
				}
			}
		}
	};

	const logoutFromActiveSession = () => {
		let allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurations.value;

		if (allInstanceAuthenticationConfigurationsData !== null) {
			const activeInstanceAuthenticationConfigurationData = activeInstanceAuthenticationConfiguration.value;

			if (activeInstanceAuthenticationConfigurationData !== null) {
				allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurationsData.filter(
					(instanceConfiguration) =>
						instanceConfiguration.name !== localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY)
				);

				localStorage.setItem(
					INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
					JSON.stringify(allInstanceAuthenticationConfigurationsData)
				);

				if (allInstanceAuthenticationConfigurationsData?.length > 0) {
					localStorage.setItem(
						ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY,
						allInstanceAuthenticationConfigurationsData[0].name
					);
				} else {
					localStorage.setItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY, '');
				}
			}
		}
	};

	const setActiveInstanceAuthenticationName = (instanceAuthenticationName: string) => {
		localStorage.setItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY, instanceAuthenticationName);
	};

	const updateTheAuthenticationToken = (token: string) => {
		const allInstanceAuthenticationConfigurationsData = allInstanceAuthenticationConfigurations.value;

		if (allInstanceAuthenticationConfigurationsData !== null) {
			const activeInstanceAuthenticationConfigurationData = activeInstanceAuthenticationConfiguration.value;

			if (activeInstanceAuthenticationConfigurationData !== null) {
				const activeInstanceAuthenticationConfigurationIndex =
					allInstanceAuthenticationConfigurationsData.findIndex(
						(instanceConfiguration) =>
							instanceConfiguration.name === activeInstanceAuthenticationConfiguration.value?.name
					);
				activeInstanceAuthenticationConfigurationData.token = token;

				allInstanceAuthenticationConfigurationsData[activeInstanceAuthenticationConfigurationIndex] =
					activeInstanceAuthenticationConfigurationData;

				localStorage.setItem(
					INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
					JSON.stringify(allInstanceAuthenticationConfigurationsData)
				);
			}
		}
	};

	const activeInstanceAuthenticationConfiguration = computed((): InstanceAuthenticationConfiguration | null => {
		if (
			localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) !== null &&
			localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) !== '' &&
			localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) !== null &&
			localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) !== ''
		) {
			const activeInstanceAuthenticationConfigurationName = localStorage.getItem(
				ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY
			);
			const allInstanceAuthenticationConfigurationsSerialized = localStorage.getItem(
				INSTANCE_AUTHENTICATION_CONFIGURATION_KEY
			) as string;

			if (allInstanceAuthenticationConfigurationsSerialized !== null) {
				try {
					const parsedInstanceAuthenticationConfigurations = JSON.parse(
						allInstanceAuthenticationConfigurationsSerialized
					) as unknown;

					if (!Array.isArray(parsedInstanceAuthenticationConfigurations)) {
						return null;
					}

					const typedInstanceAuthenticationConfigurations =
						parsedInstanceAuthenticationConfigurations as InstanceAuthenticationConfiguration[];
					const allInstanceAuthenticationConfigurations = normalizeInstanceAuthenticationConfigurations(
						typedInstanceAuthenticationConfigurations
					);

					if (
						JSON.stringify(parsedInstanceAuthenticationConfigurations) !==
						JSON.stringify(allInstanceAuthenticationConfigurations)
					) {
						localStorage.setItem(
							INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
							JSON.stringify(allInstanceAuthenticationConfigurations)
						);
					}

					const activeInstanceAuthenticationConfiguration = allInstanceAuthenticationConfigurations.find(
						(instanceConfiguration) =>
							instanceConfiguration !== null &&
							typeof instanceConfiguration.name === 'string' &&
							instanceConfiguration.name === activeInstanceAuthenticationConfigurationName
					);

					if (activeInstanceAuthenticationConfiguration !== undefined) {
						return activeInstanceAuthenticationConfiguration;
					}
				} catch {
					return null;
				}
			}
		}

		return null;
	});

	const activeInstanceAuthenticationConfigurationName = computed((): string => {
		if (localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY)) {
			return String(localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY)).toString();
		}

		return '';
	});

	const allInstanceAuthenticationConfigurations = computed((): InstanceAuthenticationConfiguration[] => {
		if (
			localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) !== null &&
			localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) !== ''
		) {
			const allInstanceAuthenticationConfigurationsSerialized = localStorage.getItem(
				INSTANCE_AUTHENTICATION_CONFIGURATION_KEY
			) as string;

			if (allInstanceAuthenticationConfigurationsSerialized !== null) {
				try {
					const parsedInstanceAuthenticationConfigurations = JSON.parse(
						allInstanceAuthenticationConfigurationsSerialized
					) as Array<InstanceAuthenticationConfiguration>;
					const normalizedInstanceAuthenticationConfigurations =
						normalizeInstanceAuthenticationConfigurations(parsedInstanceAuthenticationConfigurations);

					if (
						JSON.stringify(parsedInstanceAuthenticationConfigurations) !==
						JSON.stringify(normalizedInstanceAuthenticationConfigurations)
					) {
						localStorage.setItem(
							INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
							JSON.stringify(normalizedInstanceAuthenticationConfigurations)
						);
					}

					return normalizedInstanceAuthenticationConfigurations;
				} catch {
					/* empty */
				}
			}

			return [];
		} else {
			return [];
		}
	});

	const authenticationToken = computed((): null | string => {
		if (activeInstanceAuthenticationConfiguration.value !== null) {
			return activeInstanceAuthenticationConfiguration.value?.token;
		}

		return null;
	});

	return {
		activeInstanceAuthenticationConfiguration,
		activeInstanceAuthenticationConfigurationName,
		addInstanceAuthenticationConfiguration,
		addInstanceAuthenticationConfigurationAndActivate,
		allInstanceAuthenticationConfigurations,
		authenticationToken,
		initialize,
		logoutFromActiveSession,
		removeInstanceAuthenticationConfigurationName,
		setActiveInstanceAuthenticationName,
		updateTheAuthenticationToken,
	};
}
